import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, MapPin } from 'lucide-react';
import { useSchedule } from '@/hooks/useApi';
import { useThemeImages } from '@/hooks/useThemeImages';

const CATEGORY_COLORS: Record<string, string> = {
  opening: 'bg-purple-100 text-purple-700 border-l-purple-500',
  competition: 'bg-blue-100 text-blue-700 border-l-blue-500',
  closing: 'bg-green-100 text-green-700 border-l-green-500',
  athletesNight: 'bg-orange-100 text-orange-700 border-l-orange-500',
  ceremony: 'bg-yellow-100 text-yellow-700 border-l-yellow-500',
  other: 'bg-gray-100 text-gray-700 border-l-gray-500',
};

export default function SchedulePage() {
  const { t, i18n } = useTranslation();
  const [activeDay, setActiveDay] = useState<1 | 2 | 3>(1);
  const [venueFilter, setVenueFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const { data: schedule, isLoading } = useSchedule();
  const themeImages = useThemeImages();

  const days: { day: 1 | 2 | 3; label: string; date: string }[] = [
    { day: 1, label: t('schedule.day1'), date: t('schedule.date1') },
    { day: 2, label: t('schedule.day2'), date: t('schedule.date2') },
    { day: 3, label: t('schedule.day3'), date: t('schedule.date3') },
  ];

  const venues = useMemo(
    () => [...new Set((Array.isArray(schedule) ? schedule : []).map((e) => e.venue))],
    [schedule],
  );

  const categories = ['opening', 'competition', 'closing', 'athletesNight', 'ceremony', 'other'];

  const filteredEvents = useMemo(() => {
    if (!Array.isArray(schedule)) return [];
    return schedule.filter((e) => {
      if (e.day !== activeDay) return false;
      if (venueFilter && e.venue !== venueFilter) return false;
      if (categoryFilter && e.category !== categoryFilter) return false;
      return true;
    });
  }, [schedule, activeDay, venueFilter, categoryFilter]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex items-center gap-4 mb-2">
        <h1 className="section-title">{t('schedule.title')}</h1>
        <img src={themeImages.lineart[0]} alt="" className="w-14 h-14 object-contain hidden md:block opacity-70" />
      </div>
      <p className="section-subtitle">{t('schedule.subtitle')}</p>

      {/* Day Tabs */}
      <div className="flex gap-2 mb-8">
        {days.map((d) => (
          <button
            key={d.day}
            onClick={() => setActiveDay(d.day)}
            className={`flex-1 sm:flex-none px-6 py-3 rounded-theme font-medium text-sm transition-colors ${
              activeDay === d.day
                ? 'bg-primary text-white'
                : 'bg-bg-secondary text-text-secondary hover:bg-bg-card'
            }`}
          >
            <div className="font-bold">{d.label}</div>
            <div className="text-xs opacity-80">{d.date}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-8">
        <div>
          <label className="text-xs font-medium text-text-secondary block mb-1">
            {t('schedule.filterByVenue')}
          </label>
          <select
            value={venueFilter}
            onChange={(e) => setVenueFilter(e.target.value)}
            className="px-3 py-1.5 border border-border rounded-theme bg-bg-card text-sm"
          >
            <option value="">{t('common.all')}</option>
            {venues.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-text-secondary block mb-1">
            {t('schedule.filterByCategory')}
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-1.5 border border-border rounded-theme bg-bg-card text-sm"
          >
            <option value="">{t('common.all')}</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {t(`schedule.categories.${c}` as 'schedule.categories.opening')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Timeline */}
      {isLoading ? (
        <div className="space-y-4 animate-pulse">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-bg-secondary rounded-theme" />
          ))}
        </div>
      ) : filteredEvents.length > 0 ? (
        <div className="space-y-3">
          {filteredEvents.map((event) => {
            const colors = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.other;
            return (
              <div
                key={event.id}
                className={`card border-l-4 ${colors.split(' ').pop()}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-2 text-sm font-mono font-medium w-40 shrink-0">
                    <Clock className="w-4 h-4 text-text-secondary" />
                    {event.startTime} - {event.endTime}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold">
                      {i18n.language === 'en' ? event.titleEn : event.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-text-secondary">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {event.venue}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors}`}>
                        {t(`schedule.categories.${event.category}` as 'schedule.categories.opening')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 text-text-secondary">{t('common.noData')}</div>
      )}
    </div>
  );
}
